import { Typography } from '@mui/material'
import { Box } from '@mui/system'
import PropTypes from 'prop-types'
import { useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { DashLine } from '~/components'
import { EXTERNAL_PARTNER, SERVING_STATUS } from '~/constants'
import { selectRestaurant } from '~/redux/restaurantSlice'
import { ThemeContext } from '~/theme/ThemeProvider'
import { convertPrice, isValidArray, getTextVat } from '~/utils'

// Tóm lược thông tin thanh toán.
const SummaryPayment = ({
  subTotal,
  serviceMoney,
  servicePercent,
  vatMoney,
  vatPercent,
  total,
  listServingStatus,
  customContainerStyle,
  colorText = 'text',
  extraFee,
  discountAmount,
  isFoodCourt,
  discountVoucher,
}) => {
  const { t, i18n } = useTranslation()
  const { currentThemeObject } = useContext(ThemeContext)
  const { externalPartner } = useSelector(selectRestaurant)
  const isPartnerSmaregi = externalPartner === EXTERNAL_PARTNER.Smaregi

  const isShowSummary = useMemo(() => {
    if (isPartnerSmaregi) return true

    if (
      isValidArray(listServingStatus) &&
      listServingStatus.every((item) => item.status !== SERVING_STATUS.confirmed.value)
    )
      return false

    return true
  }, [isPartnerSmaregi, listServingStatus])

  const renderVat = () => {
    if (!vatPercent && !vatMoney) return null
    return (
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography fontSize="1.8rem" color={colorText}>
          {t(getTextVat(i18n.language))} {vatPercent > 0 && `(${vatPercent}%)`}
        </Typography>
        <Typography fontSize="1.4rem" color={colorText}>
          {convertPrice(vatMoney)}
        </Typography>
      </Box>
    )
  }

  const renderServiceFee = () => {
    if (!servicePercent && !serviceMoney) return null
    return (
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography fontSize="1.8rem" color={colorText}>
          {t('label.service_fee')} {servicePercent > 0 ? `(${servicePercent}%)` : ''}
        </Typography>
        <Typography fontSize="1.4rem" color={colorText}>
          {convertPrice(serviceMoney)}
        </Typography>
      </Box>
    )
  }

  const renderSubTotalPrice = () => {
    if (!subTotal) return null

    return (
      <Box
        sx={{ marginBottom: '10px' }}
        display="flex"
        justifyContent="space-between"
        alignItems="center">
        <Typography fontSize="1.8rem" color={colorText}>
          {t('titles.sub_total')}
        </Typography>
        <Typography fontSize="1.4rem" color={colorText}>
          {convertPrice(subTotal)}
        </Typography>
      </Box>
    )
  }

  const renderExtraFee = () => {
    if (!extraFee) return null

    return (
      <Box
        sx={{ marginBottom: '10px' }}
        display="flex"
        justifyContent="space-between"
        alignItems="center">
        <Typography fontSize="1.8rem" color={colorText} fontWeight="500">
          {t('titles.extraFee')}
        </Typography>

        <Typography fontSize="1.4rem" fontWeight="500" color={colorText}>
          {convertPrice(extraFee)}
        </Typography>
      </Box>
    )
  }

  const renderDiscountAmount = () => {
    if (!discountAmount) return null

    return (
      <Box
        sx={{ marginBottom: '10px' }}
        display="flex"
        justifyContent="space-between"
        alignItems="center">
        <Typography fontSize="1.8rem" color={colorText} fontWeight="500">
          {t('titles.discountAmount')}
        </Typography>
        <Typography fontSize="1.4rem" fontWeight="500" color={colorText}>
          {'-'}
          {convertPrice(discountAmount)}
        </Typography>
      </Box>
    )
  }

  // Hiển thị discount khi add voucher
  const renderDiscountVoucher = () => {
    if (!discountVoucher) return null

    return (
      <Box
        sx={{ marginBottom: '10px' }}
        display="flex"
        justifyContent="space-between"
        alignItems="center">
        <Typography fontSize="1.8rem" color="baseColor.color16">
          {t('titles.discount')}
        </Typography>
        <Typography fontSize="1.4rem" color="baseColor.color16">
          -{convertPrice(discountVoucher)}
        </Typography>
      </Box>
    )
  }

  const renderLastPrice = () => {
    if (isFoodCourt) return null

    return (
      <Box textAlign="right" marginTop="10px">
        <Typography color={colorText}>{t('titles.total')}</Typography>
        <Typography
          component="h2"
          fontSize="3.2rem"
          lineHeight="4rem"
          fontWeight="600"
          color={colorText}>
          {convertPrice(total)}
        </Typography>
      </Box>
    )
  }

  if (!isShowSummary) {
    return null
  }

  const renderDividerVatAndServiceFee = () => {
    if (isFoodCourt) return null

    return vatPercent || servicePercent ? <DashLine color={currentThemeObject.colorText1} /> : null
  }

  const renderDividerSubtotal = () => {
    if (isFoodCourt && !vatMoney && !serviceMoney) return null

    return <DashLine color={currentThemeObject.colorText1} />
  }

  return (
    <Box
      borderTop={`2px solid ${currentThemeObject.colorText1}`}
      borderBottom={`2px solid ${currentThemeObject.colorText1}`}
      paddingTop="20px"
      paddingBottom={isFoodCourt ? 0 : '4px'}
      sx={customContainerStyle}>
      {!isPartnerSmaregi ? (
        <>
          {renderSubTotalPrice()}
          {renderDiscountVoucher()}
          {renderDividerSubtotal()}
          <Box marginY="10px">
            {renderVat()}
            {renderServiceFee()}
          </Box>
          {renderDividerVatAndServiceFee()}
          {renderExtraFee()}
          {renderDiscountAmount()}
          {extraFee || discountAmount ? <DashLine color={currentThemeObject.colorText1} /> : null}
        </>
      ) : null}
      {renderLastPrice()}
    </Box>
  )
}

SummaryPayment.defaultProps = {
  subTotal: 0,
  serviceMoney: 0,
  servicePercent: 0,
  vatMoney: 0,
  vatPercent: 0,
  total: 0,
  listServingStatus: [],
  colorText: 'text',
  isFoodCourt: false,
  discountVoucher: 0,
}

SummaryPayment.propTypes = {
  subTotal: PropTypes.number,
  serviceMoney: PropTypes.number,
  servicePercent: PropTypes.number,
  vatMoney: PropTypes.number,
  vatPercent: PropTypes.number,
  extraFee: PropTypes.number,
  discountAmount: PropTypes.number,
  total: PropTypes.number,
  listServingStatus: PropTypes.array,
  customContainerStyle: PropTypes.any,
  colorText: PropTypes.string,
  isFoodCourt: PropTypes.bool,
  discountVoucher: PropTypes.number,
}

export default SummaryPayment
